import { MultimodalAPI } from '../../../shared/api/apiService';

export interface UploadOptions {
  userQuery?: string;
  userId?: string;
  sessionId?: string;
  language?: string;
  onProgress?: (progress: number) => void;
}

export interface UploadResult {
  success: boolean;
  file_info?: {
    filename: string;
    extraction_method: string;
    extracted_content: string;
    processing_time: number;
  };
  clarification?: any;
  error?: string;
}

/**
 * 文件上传服务类
 */
export class FileUploadService {
  /**
   * 上传单个文件
   */
  static async uploadFile(file: File, options: UploadOptions = {}): Promise<UploadResult> {
    try {
      const {
        userQuery = '',
        userId = 'anonymous',
        sessionId = null,
        language = 'ja',
        onProgress
      } = options;

      // 模拟进度更新
      onProgress?.(10);

      // 调用多模态API
      const result = await MultimodalAPI.uploadAndClarify(
        file,
        userQuery,
        userId,
        sessionId,
        language
      );

      onProgress?.(100);

      return {
        success: true,
        file_info: result.file_info,
        clarification: result.clarification
      };

    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : '文件上传失败'
      };
    }
  }

  /**
   * 批量上传文件
   */
  static async uploadFiles(
    files: File[], 
    options: UploadOptions = {}
  ): Promise<UploadResult[]> {
    const { onProgress } = options;
    const results: UploadResult[] = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // 计算总体进度
      const overallProgress = ((i / files.length) * 100);
      onProgress?.(overallProgress);
      
      // 上传单个文件
      const result = await this.uploadFile(file, {
        ...options,
        onProgress: (fileProgress) => {
          const totalProgress = overallProgress + (fileProgress / files.length);
          onProgress?.(totalProgress);
        }
      });
      
      results.push(result);
    }
    
    onProgress?.(100);
    return results;
  }

  /**
   * 验证文件类型
   */
  static isValidFileType(file: File): boolean {
    const supportedTypes = [
      // 图片类型
      'image/jpeg',
      'image/jpg', 
      'image/png',
      'image/gif',
      'image/webp',
      // 音频类型
      'audio/mpeg',
      'audio/mp3',
      'audio/wav',
      'audio/m4a',
      'audio/ogg',
      'audio/webm',
      // 文档类型
      'application/pdf'
    ];

    return supportedTypes.includes(file.type);
  }

  /**
   * 验证文件大小
   */
  static isValidFileSize(file: File, maxSize: number = 10 * 1024 * 1024): boolean {
    return file.size <= maxSize;
  }

  /**
   * 获取文件类型
   */
  static getFileCategory(file: File): 'image' | 'audio' | 'document' | 'unknown' {
    const type = file.type;
    
    if (type.startsWith('image/')) {
      return 'image';
    } else if (type.startsWith('audio/')) {
      return 'audio';
    } else if (type === 'application/pdf') {
      return 'document';
    }
    
    return 'unknown';
  }

  /**
   * 创建文件预览URL
   */
  static createPreviewUrl(file: File): string {
    return URL.createObjectURL(file);
  }

  /**
   * 清理预览URL
   */
  static revokePreviewUrl(url: string): void {
    if (url && url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  }

  /**
   * 格式化文件大小
   */
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }
} 